import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { assistantService, authService } from '../utils/api';

function General() {
  const { assistantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assistant, setAssistant] = useState(null);
  const [form, setForm] = useState({
    user_speech_timeout: '',
    webhook_url: '',
    name_transliteration_enabled: false
  });

  const userRole = authService.getUserRole();
  const canEdit = userRole === 'super_admin' || userRole === 'admin';

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const data = await assistantService.getAssistantById(assistantId);
        if (!isMounted) return;
        setAssistant(data);
        setForm({
          user_speech_timeout: data.user_speech_timeout ?? '',
          webhook_url: data.webhook_url || '',
          name_transliteration_enabled: data.name_transliteration_enabled ?? false
        });
      } catch (e) {
        toast.error(e?.message || 'Failed to load assistant general settings');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    if (assistantId) load();
    return () => {
      isMounted = false;
    };
  }, [assistantId]);

  const title = useMemo(
    () => assistant?.assistant_name || assistant?.name || 'Assistant',
    [assistant]
  );

  const handleChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        user_speech_timeout:
          form.user_speech_timeout === '' ? undefined : Number(form.user_speech_timeout),
        webhook_url: form.webhook_url?.trim() || undefined,
        name_transliteration_enabled: form.name_transliteration_enabled
      };
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

      const updated = await assistantService.updateAssistant(assistantId, payload);
      toast.success('General settings saved');

      // keep session selected assistant in sync (nice for header)
      const selected = assistantService.getSelectedAssistant();
      if (selected?.id === assistantId) {
        assistantService.setSelectedAssistant({ ...selected, ...updated });
      }
    } catch (e) {
      toast.error(e?.message || e?.detail || 'Failed to save general settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', p: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              General
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              {title}
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Card elevation={0}>
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="User speech timeout (s)"
                      value={form.user_speech_timeout}
                      onChange={(e) => handleChange('user_speech_timeout', e.target.value)}
                      disabled={!canEdit}
                      inputProps={{ min: 0, step: 0.1 }}
                      placeholder="e.g. 0.5"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Webhook URL"
                      value={form.webhook_url}
                      onChange={(e) => handleChange('webhook_url', e.target.value)}
                      disabled={!canEdit}
                      placeholder="https://example.com/webhook"
                      helperText="End-of-call report is POSTed here. If empty, no report is sent."
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!form.name_transliteration_enabled}
                          onChange={(e) =>
                            handleChange('name_transliteration_enabled', e.target.checked)
                          }
                          disabled={!canEdit}
                        />
                      }
                      label="Transliterate name to Hindi"
                    />
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      When on, the "name" variable is converted from English to Hindi (Devanagari)
                      before a call so TTS pronounces it correctly.
                    </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!canEdit || saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </Box>

                {!canEdit && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 1, display: 'block' }}
                  >
                    View-only access. Contact an admin to edit.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Container>
      </Box>
    </DashboardLayout>
  );
}

export default General;
